import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "antd";
import styles from "./styles.module.scss";
import Plus from "@assets/icons/whitePlus";
import Serch from "@assets/icons/search";
import CustomDropdownDays from "@components/DropdownDays";
import { GoalCard } from "./TaskToDo";
import { initialStepsData } from "./TaskToDo/mock";
import { ActiveStepStore } from "src/store/onboarding.store";

export interface Subtask {
  id: number;
  title: string;
  description?: string;
  completed?: boolean;
}

export interface Task {
  id: number;
  title: string;
  date: string;
  stepsCount: number;
  status: "new" | "done";
  subtasks: Subtask[];
}

export function TaskManagerBlock() {
  const step = ActiveStepStore((state) => state.step);
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<Task[]>(initialStepsData);
  const [hideCompleted, setHideCompleted] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    if (step >= 6 && step <= 8) {
      setTasks((prev) =>
        prev.map((task, index) => {
          if (index !== 0) return task;

          let completedCount = 0;
          if (step === 6) completedCount = 1;
          if (step === 7) completedCount = 2;
          if (step === 8) completedCount = task.subtasks.length;

          const updatedSubtasks = task.subtasks.map((subtask, i) => ({
            ...subtask,
            completed: i < completedCount,
          }));

          return {
            ...task,
            subtasks: updatedSubtasks,
            status: completedCount === task.subtasks.length ? "done" : "new",
          };
        }),
      );
    }
  }, [step]);

  useEffect(() => {
    if (step === 8) {
      setTasks((prev) =>
        prev.map((task, index) => {
          if (index === 0) {
            const updatedSubtasks = task.subtasks.map((subtask) => ({
              ...subtask,
              completed: true,
            }));
            return {
              ...task,
              status: "done",
              subtasks: updatedSubtasks,
            };
          }
          return task;
        }),
      );
    }
  }, [step]);

  const handleToggleSubtask = (taskId: number, subtaskId: number) => {
    setTasks((prevTasks) =>
      prevTasks.map((task) => {
        if (task.id !== taskId) return task;

        const index = task.subtasks.findIndex((st) => st.id === subtaskId);
        if (index === -1) return task;

        if (
          index > 0 &&
          !task.subtasks
            .slice(0, index)
            .every((prevSubtask) => prevSubtask.completed)
        ) {
          return task;
        }

        const toggled = !task.subtasks[index].completed;

        const updatedSubtasks = task.subtasks.map((subtask, i) => {
          if (i < index) return subtask;
          if (i === index) return { ...subtask, completed: toggled };
          return { ...subtask, completed: false };
        });

        const allCompleted = updatedSubtasks.every((st) => st.completed);
        return {
          ...task,
          subtasks: updatedSubtasks,
          status: allCompleted ? "done" : "new",
        };
      }),
    );
  };

  const filteredTasks = tasks.filter((task) => {
    const matchesSearch = searchQuery.trim()
      ? task.title.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    const matchesHideCompleted = hideCompleted ? task.status !== "done" : true;
    return matchesSearch && matchesHideCompleted;
  });

  return (
    <div>
      <header className={styles.taskManagerContainer}>
        <h1 className={styles.headerTitle}>{t("Task Manager")}</h1>
        <button className={styles.bntTask}>
          <Plus />
          {t("Add Task")}
        </button>
      </header>
      <div className={styles.completedSwitch}>
        <div className={styles.searchWrapper}>
          <Serch />
          <input
            type="text"
            placeholder={t("Search task")}
            className={styles.searchWrapperInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className={styles.dateBetween}>
          <div className={styles.switchContainer}>
            <Switch onChange={setHideCompleted} />
            <span className={styles.switchLabel}>{t("Hide Completed")}</span>
          </div>
          <CustomDropdownDays />
        </div>
      </div>
      <div className={styles.goalBlock}>
        <GoalCard
          stepsData={filteredTasks}
          onToggleSubtask={handleToggleSubtask}
        />
      </div>
    </div>
  );
}
